import { ApiError, type CustomerApi } from "./api";

const STORAGE_KEY = "gobro-favorites";
const PENDING_KEY = "gobro-favorites-pending";

export interface PendingFavoriteOp {
  favorite: boolean;
  storeId: string;
}

export function readFavorites(): Set<string> {
  try {
    return new Set<string>(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]") as string[]);
  } catch {
    return new Set<string>();
  }
}

export function readPendingFavoriteOps(): PendingFavoriteOp[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(PENDING_KEY) ?? "[]") as PendingFavoriteOp[];
    return Array.isArray(parsed)
      ? parsed.filter((op) => typeof op?.storeId === "string" && typeof op?.favorite === "boolean")
      : [];
  } catch {
    return [];
  }
}

export function toggleFavoriteLocal(storeId: string): boolean {
  const values = readFavorites();
  if (values.has(storeId)) values.delete(storeId);
  else values.add(storeId);
  writeFavorites(values);
  return values.has(storeId);
}

export function recordPendingFavoriteOp(op: PendingFavoriteOp): void {
  const pending = readPendingFavoriteOps().filter((entry) => entry.storeId !== op.storeId);
  pending.push(op);
  writePendingFavoriteOps(pending);
}

export async function syncFavorites(api: CustomerApi): Promise<Set<string>> {
  const server = await api.favorites();
  const serverIds = new Set(server.items.map((item) => item.storeId));
  const local = readFavorites();
  const merged = new Set([...serverIds, ...local]);
  for (const storeId of local) {
    if (serverIds.has(storeId)) continue;
    try {
      await api.addFavorite(storeId);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) merged.delete(storeId);
      else throw error;
    }
  }
  const remaining: PendingFavoriteOp[] = [];
  for (const op of readPendingFavoriteOps()) {
    try {
      if (op.favorite) {
        await api.addFavorite(op.storeId);
        merged.add(op.storeId);
      } else {
        await api.removeFavorite(op.storeId);
        merged.delete(op.storeId);
      }
    } catch (error) {
      if (op.favorite && error instanceof ApiError && error.status === 404) {
        merged.delete(op.storeId);
      } else {
        remaining.push(op);
      }
    }
  }
  writeFavorites(merged);
  writePendingFavoriteOps(remaining);
  return merged;
}

function writeFavorites(values: ReadonlySet<string>): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...values]));
}

function writePendingFavoriteOps(ops: PendingFavoriteOp[]): void {
  if (ops.length) localStorage.setItem(PENDING_KEY, JSON.stringify(ops));
  else localStorage.removeItem(PENDING_KEY);
}
